<script lang="ts">
  import { dev } from '$app/env'
  import { resolve } from '$app/paths'
  import { isNonEmpty } from '#utils/text.js'
  import SanityImage from '#lib/components/SanityImage.svelte'
  import CTA from '#lib/components/CTA.svelte'
  import type { Artist } from '#types'

  type Props = {
    artist: Artist
    processMaterials: (materials: string[]) => string[]
    priority?: boolean
  }

  let { artist, processMaterials, priority = false }: Props = $props()

  const portraitUrl = $derived(artist.portrait?.asset?.url)
</script>

<!-- Artist Card -->
<div class="mb-12 overflow-hidden border border-gray-200 bg-white shadow-lg">
  <div class="grid grid-cols-1 gap-0 lg:grid-cols-2">
    <!-- Artist Image -->
    <div class="relative lg:col-span-1">
      {#if isNonEmpty(portraitUrl)}
        <div class="aspect-square h-full w-full">
          <SanityImage
            src={portraitUrl}
            alt={artist.name}
            className="w-full h-full object-cover"
            quality={80}
            width={750}
            height={750}
            loading={priority ? 'eager' : 'lazy'}
            fetchpriority={priority ? 'high' : undefined}
          />
        </div>
      {:else}
        <div
          class="flex aspect-square w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
        >
          <span class="text-lg text-black">No portrait available</span>
        </div>
      {/if}
    </div>

    <!-- Artist Info -->
    <div class="flex flex-col justify-center gap-8 p-10 lg:col-span-1">
      <div class="space-y-5">
        <!-- Preserved Artworks Badge -->
        <div
          class="border-brand-olive flex w-fit items-center gap-2.5 border-l-2 px-2.5 py-1 text-black lg:px-5 lg:py-2.5"
        >
          <div class="text-lg font-bold lg:text-2xl">{artist.artworks?.length ?? 0}</div>
          <div class="text-sm lg:text-base">Preserved Artworks</div>
        </div>

        <div class="mb-5 lg:mb-10">
          <h2 class="mb-2.5 text-3xl font-bold text-black lg:mb-5 lg:text-5xl">{artist.name}</h2>
          {#if (artist.birthYear ?? 0) > 0 || (artist.deathYear ?? 0) > 0}
            <p class="mb-2.5 text-lg text-black lg:mb-5 lg:text-xl">
              {artist.birthYear}{(artist.deathYear ?? 0) > 0
                ? ` - ${String(artist.deathYear)}`
                : ' - present'}
            </p>
          {/if}
          {#if isNonEmpty(artist.description)}
            <p class="mt-4 line-clamp-3 text-base leading-relaxed text-black/80 lg:mt-6 lg:text-lg">
              {artist.description}
            </p>
          {/if}
        </div>

        {#if dev && artist.materials && artist.materials.length > 0}
          {@const processedMaterials = processMaterials(artist.materials)}
          {#if processedMaterials.length > 0}
            <div class="mb-5 text-black lg:mb-10">
              <p class="mb-2.5 text-base font-semibold text-black lg:mb-5 lg:text-xl">
                Materials + Techniques:
              </p>
              <div class="flex flex-wrap gap-2">
                {#each processedMaterials as material (material)}
                  <span
                    class="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-black lg:px-4 lg:py-2 lg:text-lg"
                  >
                    {material}
                  </span>
                {/each}
              </div>
            </div>
          {/if}
        {/if}
      </div>

      <!-- Social Links and Profile Button -->
      <div class="flex flex-col gap-5 max-lg:pt-10 lg:flex-row lg:items-center lg:justify-between">
        <CTA
          text="View Artist Profile"
          variant="secondary"
          href={resolve('/artists/[handle]', { handle: artist.handle.current })}
          className="inline-flex gap-2 items-center justify-center px-2.5 lg:px-10"
          data-umami-event="artist-card-click"
          data-umami-event-artist={artist.name}
        ></CTA>
      </div>
    </div>
  </div>
</div>
